import ChatIcon from '@material-ui/icons/Chat';
import BarChartIcon from '@material-ui/icons/BarChart';
import HistoryIcon from '@material-ui/icons/History';
import DashboardIcon from '@material-ui/icons/Dashboard';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Grid, Button, Box } from '@material-ui/core';
import SecurityIcon from '@material-ui/icons/Security';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEffect, useState } from 'react';
import { Content, Header, InfoCard, Page, Progress } from '@backstage/core-components';

const AdminPage = () => {
    const navigate = useNavigate();
    const catalogApi = useApi(catalogApiRef);
    const config = useApi(configApiRef);
    const identityApi = useApi(identityApiRef);
    const [loading, setLoading] = useState(true);
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

    useEffect(() => {
        const checkPermissionAndFetchMetrics = async () => {
            try {
                console.log('🔍 Admin Page: Starting permission check...');

                // Check if user is platform_admin
                const identity = await identityApi.getBackstageIdentity();
                const userName = identity.userEntityRef.split('/')[1];
                const userRef = identity.userEntityRef;

                console.log('👤 Administration Page: Checking for user:', userName);

                // Get user email
                let userEmail = userName;
                try {
                    const userEntity = await catalogApi.getEntityByRef(userRef);
                    userEmail = (userEntity?.spec?.profile as any)?.email || userName;
                } catch (err) {
                    console.warn('⚠️ Administration Page: Could not fetch user entity:', err);
                }

                // Check for impersonated user (for development/testing)
                const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
                const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
                const impersonatedEmail = impersonatedFromStorage || impersonatedFromConfig;

                // Use impersonated email if available
                let effectiveUserEmail = userEmail;
                if (impersonatedEmail && impersonatedEmail !== userName) {
                    console.log('🎭 Administration Page: Using impersonated user:', impersonatedEmail);
                    effectiveUserEmail = impersonatedEmail;
                }

                console.log('📧 Administration Page: Effective user email:', effectiveUserEmail);

                const backendUrl = config.getString('backend.baseUrl');
                const token = (await identityApi.getCredentials()).token;

                console.log('🔐 Administration Page: Checking platform_admin permission via OpenFGA...');

                const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        user: `user:${effectiveUserEmail}`,
                        action: 'platform_admin',
                        resource: 'platform:main',
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    const isAdmin = data.allowed || false;
                    setIsPlatformAdmin(isAdmin);

                    console.log(`${isAdmin ? '✅' : '❌'} Administration Page: User is ${isAdmin ? '' : 'NOT '}platform_admin`);
                    console.log('Response data:', data);

                } else {
                    const errorText = await response.text();
                    console.warn('⚠️ Administration Page: Permission check failed:', response.status, errorText);
                }
            } catch (error) {
                console.error('❌ Administration Page: Error checking permissions:', error);
            } finally {
                setLoading(false);
            }
        };

        checkPermissionAndFetchMetrics();
    }, [catalogApi, config, identityApi]);

    if (loading) {
        return (
            <InfoCard title="Administration Page">
                <Box p={2}>
                    <Progress />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                        Checking permissions...
                    </Typography>
                </Box>
            </InfoCard>
        );
    }

    if (!isPlatformAdmin) {
        console.log('🔒 Admin Page: User is not platform_admin, showing access denied');
        return (
            <div style={{ padding: 32 }}>
                <InfoCard title="Administration Page">
                    <Box p={2}>
                        <Typography variant="h6" gutterBottom color="error">
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            This page is only accessible to platform administrators.
                            If you believe you should have access, please contact your system administrator.
                        </Typography>
                    </Box>
                </InfoCard>
            </div>
        );
    }

    return (
        <Page themeId="tool">
            <Header title="Administration Page" subtitle="Tools and utilities" />
            <Content>
                <Typography variant="h4" gutterBottom>
                    Administración
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <BarChartIcon style={{ fontSize: 40 }} color="primary" />
                                <Typography variant="h6">Platform Insights</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Estadísticas y métricas de la plataforma.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 16 }}
                                    onClick={() => navigate('/insights')}
                                >
                                    Ir a Platform Insights
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <ChatIcon style={{ fontSize: 40 }} color="primary" />
                                <Typography variant="h6">Agent Forge</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Gestión y acceso a Agent Forge.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 16 }}
                                    onClick={() => navigate('/agent-forge')}
                                >
                                    Ir a Agent Forge
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <SecurityIcon style={{ fontSize: 40 }} color="primary" />
                                <Typography variant="h6">Gestión de Roles</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Administración de roles y permisos.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 16 }}
                                    onClick={() => navigate('/manage-roles')}
                                >
                                    Ir a Gestión de Roles
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <HistoryIcon style={{ fontSize: 40 }} color="primary" />
                                <Typography variant="h6">Activity Log</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Registro de actividad de la plataforma.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 16, marginRight: 8 }}
                                    onClick={() => navigate('/activity-log')}
                                >
                                    Ir a Activity Log
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    style={{ marginTop: 16 }}
                                    onClick={() => navigate('/activity-log/dashboard')}
                                >
                                    Dashboard
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <AnnouncementIcon style={{ fontSize: 40 }} color="primary" />
                                <Typography variant="h6">Gestión de Anuncios</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Administración de anuncios.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 16 }}
                                    onClick={() => navigate('/announcements-admin')}
                                >
                                    Ir a Gestión de Anuncios
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <DashboardIcon style={{ fontSize: 40 }} color="primary" />
                                <Typography variant="h6">K8S Dashboard</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    K8S Dashboard de Grafana 
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    style={{ marginTop: 16 }}
                                    onClick={() => navigate('/catalog/default/component/pro-sa-aws-paud')}
                                >
                                    Dashboard
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Content>
        </Page>
    );
};

export default AdminPage;
